import { useState } from 'react';
import { generateErrorCsv, downloadErrorCsvFile } from '../../utils/errorCsvUtils';
import { validatePanelCsv } from './utils';

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file.rawFile);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const getFileInfo = file => ({
  name: file.rawFile.name,
  size: file.rawFile.size,
  lastModified: file.rawFile.lastModified,
});

export const usePanelCsvValidation = ({ notify }) => {
  const [csvValidation, setCsvValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [errorCsv, setErrorCsv] = useState(null);
  const [errorFileInfo, setErrorFileInfo] = useState(null);
  const [successFileInfo, setSuccessFileInfo] = useState(null);

  const resetValidationState = () => {
    setCsvValidation(null);
    setErrorCsv(null);
    setErrorFileInfo(null);
    setSuccessFileInfo(null);
    setIsValidating(false);
  };

  const validateCsv = async (file) => {
    if (!file) {
      resetValidationState();
      return;
    }

    setIsValidating(true);
    setErrorCsv(null);
    setErrorFileInfo(null);
    setSuccessFileInfo(null);

    try {
      const csvText = await readFileAsText(file);
      const originalHeaders = csvText.split('\n')[0]?.split(';') || [];
      const validation = await validatePanelCsv(csvText, originalHeaders);

      if (validation?.error) {
        setCsvValidation(validation);
        setErrorFileInfo(getFileInfo(file));
        notify(`CSV inválido`, { type: 'error' });
        return;
      }

      setCsvValidation(validation);

      if (validation.valid || validation.isValid) {
        setSuccessFileInfo(getFileInfo(file));
        notify('Arquivo CSV validado com sucesso.', { type: 'success' });
        return;
      }

      const errorsCsv = generateErrorCsv(
        csvText,
        validation.validationResults,
        originalHeaders,
        {
          statusColumnName: 'Status',
          errorColumnName: 'Motivo(s) do(s) erro(s)',
        }
      );

      setErrorCsv(errorsCsv);
      setErrorFileInfo(getFileInfo(file));
    } catch (error) {
      setCsvValidation({
        isValid: false,
        error: error.message,
      });
      notify('Erro ao processar o arquivo CSV.', { type: 'error' });
    } finally {
      setIsValidating(false);
    }
  };

  const handleDownloadErrorCsv = (fileInfo) => {
    downloadErrorCsvFile(errorCsv, `${fileInfo.name}-erros-validacao.csv`);
  };

  const hasSuccessfulCsvValidation = Boolean(
    (csvValidation?.valid || csvValidation?.isValid) && !csvValidation?.error
  );
  const hasCsvErrors = Boolean(
    csvValidation &&
    !(csvValidation.valid || csvValidation.isValid) &&
    !csvValidation.error &&
    errorCsv
  );

  return {
    csvValidation,
    isValidating,
    errorCsv,
    errorFileInfo,
    successFileInfo,
    hasSuccessfulCsvValidation,
    hasCsvErrors,
    validateCsv,
    handleDownloadErrorCsv,
  };
};

export default usePanelCsvValidation;
